<!-- eslint-disable import/no-unresolved -->
<template>
  <section class="change-password-section">
    <Preloader v-if="isLoading" />
    <div class="change-password-wrapper" :class="[`bg-${$route.meta.background}`]">
      <img class="es-logo" src="@/assets/images/brand/logo_full.svg" />
      <form class="change-password" @submit.prevent="submit">
        <div class="change-password__logo">
          <h3 class="change-password__title">{{ $t("auth.change_password") }}</h3>
          <div class="change-password__description-wrapper">
            <img class="information-icon" alt="Information icon" src="@/assets/images/icons/i_badge.svg" />
            <p class="change-password__description">
              Za pomocą nowego hasła będziesz logować się także do Early Stage MUSIC.
            </p>
          </div>
        </div>
        <div class="change-password__inputs">
          <PasswordInput v-model="oldPassword" type="password" name="password" @input="errorOldPassword = ''"
            passwordEye :error="errorOldPassword">{{ $t("auth.old_password") }}
          </PasswordInput>
          <PasswordInput type="password" name="password" passwordEye v-model.trim="$v.password.$model"
            @input="delayTouch($v.password)" :error="getPasswordError()">{{ $t("auth.new_password") }}
          </PasswordInput>
          <div class="login__validation">
            <p class="text-body-small-long">Twoje hasło musi zawierać:</p>
            <div class="login__validation-wrapper">
              <div class="login__validation-item">
                <img :src="passwordValidation.minLength
                    ? require('@/assets/images/login/login-password-check.svg')
                    : require('@/assets/images/login/login-password-mark.svg')
                  " alt="check" />
                <span class="text-body-small-long">minimum 10 znaków</span>
              </div>
              <div class="login__validation-item">
                <img :src="passwordValidation.specialChar
                    ? require('@/assets/images/login/login-password-check.svg')
                    : require('@/assets/images/login/login-password-mark.svg')
                  " alt="check" />
                <span class="text-body-small-long">co najmniej 1 znak specjalny</span>
              </div>
              <div class="login__validation-item">
                <img :src="passwordValidation.upperLowerCase
                    ? require('@/assets/images/login/login-password-check.svg')
                    : require('@/assets/images/login/login-password-mark.svg')
                  " alt="check" />
                <span class="text-body-small-long">co najmniej 1 małą i dużą literę</span>
              </div>
              <div class="login__validation-item">
                <img :src="passwordValidation.digit
                    ? require('@/assets/images/login/login-password-check.svg')
                    : require('@/assets/images/login/login-password-mark.svg')
                  " alt="check" />
                <span class="text-body-small-long">co najmniej 1 cyfrę</span>
              </div>
            </div>
          </div>
          <PasswordInput type="password" name="passwordConfirmation" passwordEye
            v-model.trim="$v.passwordConfirmation.$model" @input="delayTouch($v.passwordConfirmation)"
            :error="getPasswordConfirmationError()">{{ $t("auth.new_password_confirmation") }}
          </PasswordInput>
        </div>
        <transition name="fade">
          <div v-if="showNotifications" class="password-notification">
            <div v-if="isFinished && !error && !errorOldPassword">
              <Notification description="Zmieniono hasło" type="success" />
            </div>
            <div v-if="error || errorOldPassword">
              <Notification :description="error || errorOldPassword" type="error" />
            </div>
          </div>
        </transition>
        <Button :disabled="isSubmiting ||
          !Object.values(passwordValidation).every(Boolean) ||
          !oldPassword ||
          !passwordConfirmation
          " class="btn_primary submit-button">
          {{ $t("auth.save_password") }}
        </Button>
      </form>
    </div>
  </section>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import { validationMixin } from 'vuelidate';
import {
  required, minLength, sameAs, helpers,
} from 'vuelidate/lib/validators';
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
// eslint-disable-next-line import/no-cycle
import VueStore from '@/store';
import Preloader from '@/components/Preloader.vue';
import Button from '@/components/Form/BaseButton.vue';
import PasswordInput from '@/components/Form/BasePasswordInput.vue';
import Notification from '@/components/Notification/Notification.vue';

const touchMap = new WeakMap();

const containsNumber = helpers.regex('containsNumber', /[0-9]/);
const containsLetters = helpers.regex('containsLetters', /[A-Za-z]/);

export default {
  name: 'ChangePassword',
  mixins: [validationMixin],
  validations: {
    password: {
      required,
      minLength: minLength(8),
      containsNumber,
      containsLetters,
    },
    passwordConfirmation: {
      required,
      sameAsPassword: sameAs('password'),
    },
  },
  data() {
    return {
      oldPassword: null,
      errorOldPassword: null,
      password: null,
      passwordConfirmation: null,
      isFinished: false,
      error: '',
      isSubmiting: false,
      showNotifications: false,
    };
  },
  components: {
    Button,
    PasswordInput,
    Preloader,
    Notification,
  },

  computed: {
    ...mapState('students', ['student']),
    ...mapState('layout', ['isLoading']),
    ...mapGetters('auth', ['isFullAccess']),
    passwordValidation() {
      const password = this.password || '';
      return {
        minLength: password.length >= 10,
        specialChar: /\W+/g.test(password),
        upperLowerCase: /[a-z]/.test(password) && /[A-Z]/.test(password),
        digit: /[0-9]+/g.test(password),
      };
    },
    routeName() {
      if (this.student !== null) {
        if (Object.prototype.hasOwnProperty.call(this.student, 'group')) {
          return {
            name: 'Main.Attendances',
            params: { kidId: this.student.id },
          };
        }
        return {
          name: 'Main.OnlineMaterials',
        };
      }
      return { name: 'Auth.Welcome' };
    },
  },

  beforeRouteEnter(to, from, next) {
    if (VueStore.getters['auth/isOfficeLogin']) {
      VueStore.commit('auth/clearAuth');
      VueStore.dispatch('auth/clearStore');
      next({ name: 'Auth.Login' });
    } else next();
  },

  methods: {
    ...mapActions('auth', ['changePassword']),
    ...mapMutations('layout', ['closeSidebar']),

    getPasswordError() {
      if (this.$v.password.$dirty) {
        if (!this.$v.password.required) {
          return this.$t('validation.password.required');
        }
        if (this.$v.password.$error && this.$v.password.$model !== '') {
          return [this.$t('validation.password.general')].join(' ');
        }
      }
      return undefined;
    },

    getPasswordConfirmationError() {
      if (this.$v.passwordConfirmation.$dirty) {
        if (!this.$v.passwordConfirmation.required) {
          return this.$t('validation.password.required');
        }
        if (this.$v.passwordConfirmation.$error) {
          return this.$t('validation.password.same');
        }
      }
      return undefined;
    },

    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },

    async submit() {
      this.isSubmiting = true;
      this.showNotifications = false;
      this.error = '';
      try {
        await this.changePassword({
          oldPassword: this.oldPassword,
          password: this.password,
          passwordConfirmation: this.passwordConfirmation,
        });
        this.showNotifications = true;
        this.isFinished = true;

        this.oldPassword = null;
        this.errorOldPassword = null;
        this.password = null;
        this.passwordConfirmation = null;
        this.$v.$reset();
      } catch (e) {
        if (e?.response && e?.response?.status === 400) {
          this.errorOldPassword = e.response.data.errors.fields.oldPassword;
          this.error = e.response.data.errors.fields.password;
          this.showNotifications = true;
          console.log(
            `Wystąpił błąd(${e.response.status}) - ${this.error || this.errorOldPassword}`,
          );
        } else {
          console.log(e);
        }
      } finally {
        this.isSubmiting = false;
      }
    },
  },

  async created() {
    this.closeSidebar();
    this.$store.commit('layout/startLoading');
  },
  mounted() {
    document.body.classList.add('hide-class-list');
    document.body.classList.add('dont-show-class-list');
    setTimeout(() => {
      this.$store.commit('layout/finishLoading');
    }, 300);
  },
  beforeDestroy() {
    document.body.classList.remove('dont-show-class-list');
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.es-logo {
  display: none;

  @media screen and (min-width: 768px) {
    display: block;
    max-width: 11rem;
  }
}

.change-password-section {
  margin-left: auto;
  overflow: hidden;
  min-height: 100%;
  position: relative;
  z-index: 1;

  @media screen and (min-width: 768px) {
    width: calc(100% - 312px);
    padding-top: 30px;
  }
}

.change-password-wrapper {
  @apply flex flex-col justify-center items-center;
  gap: 2.5rem;
}

.submit-button {
  margin-top: 2rem;

  @media screen and (min-width: 768px) {
    max-width: 15rem;
    margin: 0 auto;
  }
}

.change-password {
  @apply relative z-10 flex flex-col bg-white;
  border-radius: 10px;
  max-width: 640px;
  padding-left: 1rem;
  padding-right: 1rem;
  min-height: 100vh;
  padding-top: 8rem;
  padding-bottom: 8rem;
  justify-content: center;

  @media screen and (min-width: 768px) {
    padding-top: 3rem;
    padding-bottom: 3rem;
    padding-left: 4rem;
    padding-right: 4rem;
    min-height: unset;
    justify-content: unset;
  }

  &__inputs {
    margin-top: 48px;
  }

  &__logo {
    @apply flex flex-col;
    gap: 2rem;

    @media screen and (min-width: 768px) {
      gap: 3rem;
    }
  }

  &__title {
    @apply font-bold text-2xl text-center block;

    @media screen and (min-width: 700px) {
      @apply text-4xl;
    }
  }

  &__description-wrapper {
    align-items: flex-start;
    display: flex;
    gap: 0.75rem;
  }

  &__description {
    @apply font-normal text-grey-2;
    line-height: 20px;
  }
}

.password-notification {
  margin-bottom: 24px;
}

.login {
  &__validation {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
    margin-bottom: 24px;

    &-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
    }

    &-item {
      display: flex;
      align-items: center;
      gap: 8px;
      align-self: stretch;
    }
  }
}
</style>
